<script setup>
import {useStore} from "vuex";
import {computed, ref} from "vue";
import AddCardDetails from "@/components/billing/AddCardDetails.vue";
import ProgressSteps from "@/components/website/ProgressSteps.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";

const emit = defineEmits(['back']);
const store = useStore();
const showPaymentForm = ref(false);
const addCardForm = ref(null);

const props = defineProps({
  price: {
    required: true,
    type: String,
  },
  vat: {
    required: true,
    type: String,
  },
  cardHolderName: {
    required: true,
    type: String,
  },
  requiresPaymentDetails: {
    required: true,
    type: Boolean,
  },
});

const needsCardAdding = ref(props.requiresPaymentDetails);
const loading = computed(() => store.getters['appsumoWebsite/loading']);
const billingProcessing = computed(() => store.getters['billing/processing']);

const handleContinue = async() => {
  await store.dispatch('appsumoWebsite/create');
}

const handleAddCard = async () => {
  const passes = await addCardForm.value.requiredFieldsSelected();

  if (!passes) {
    return;
  }

  await store.dispatch('billing/createStripeCustomer', addCardForm.value.currency);

  if (store.getters['billing/cardError']) {
    return;
  }

  showPaymentForm.value = false;
  needsCardAdding.value = false;
}

const handleBack = () => {
  store.dispatch('appsumoWebsite/setErrors', {});

  emit('back');
}
</script>

<template>
  <div
    class="tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-text-center tw-max-w-default-container tw-mx-auto tw-py-6 tw-min-h-[680px]"
  >
    <AddCardDetails
      v-if="showPaymentForm"
      ref="addCardForm"
      :show-notice="false"
      :card-holder-name="cardHolderName"
    >
      <template #buttons>
        <div class="tw-flex tw-w-full tw-justify-between">
          <DefaultButton
            variant="base"
            :disabled="billingProcessing"
            @click="showPaymentForm = false"
          >
            Back
          </DefaultButton>

          <DefaultButton
            class="tw-w-48"
            :disabled="billingProcessing"
            @click="handleAddCard"
          >
            Submit
          </DefaultButton>
        </div>
      </template>
    </AddCardDetails>

    <div
      v-else
      class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6 tw-gap-8 tw-max-w-[760px] tw-w-3/4"
    >
      <div
        class="tw-flex tw-flex-col tw-justify-between tw-w-full tw-items-center tw-border tw-border-grey-mid tw-rounded-xl tw-gap-y-10 tw-px-14"
        :class="{
          'tw-py-20': requiresPaymentDetails,
          'tw-py-28': !requiresPaymentDetails,
        }"
      >
        <span class="tw-text-blue tw-flex tw-gap-2.5 tw-rounded-xl tw-bg-blue-highlight tw-p-3">
          <svg-vue
            class="tw-w-20"
            icon="icons/info"
          />
        </span>
        <p class="tw-text-grey-dark tw-text-3xl tw-mb-8">
          You will be billed
          <span class="tw-font-bold">
            {{ price }}
          </span>
          <span
            v-if="vat"
            class="!tw-text-2xl"
          >
            ({{ vat }} inc. VAT)
          </span>
          per month for each additional website you add.
        </p>

        <p
          v-if="requiresPaymentDetails"
          class="tw-text-grey-dark tw-text-3xl"
        >
          To complete this transaction, please add a payment card to your account.
        </p>

        <DefaultButton
          v-if="requiresPaymentDetails"
          :disabled="loading"
          class="tw-w-full tw-max-w-[360px]"
          @click="showPaymentForm = true"
        >
          Add Payment Card
        </DefaultButton>
      </div>

      <div class="tw-flex tw-w-full tw-justify-between">
        <DefaultButton
          variant="base"
          :disabled="loading"
          @click="handleBack"
        >
          Back
        </DefaultButton>

        <DefaultButton
          :disabled="loading || needsCardAdding"
          @click="handleContinue"
        >
          Add Website
        </DefaultButton>
      </div>

      <div class="tw-flex tw-flex-col">
        <ProgressSteps :current-step="3" />
      </div>
    </div>
  </div>
</template>
